import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { EnvService } from 'src/app/env.service';
import { NotEnoughCreditComponent } from 'src/app/shared/components/snackbar/not-enough-credit/not-enough-credit.component';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ChunkService } from '../../content/chunk/chunk.service';
import { MySpaceService } from '../my-space.service';

@Component({
  selector: 'app-app-photo-editor',
  templateUrl: './app-photo-editor.component.html',
  styleUrls: ['./app-photo-editor.component.scss'],
  standalone: false,
})
export class AppPhotoEditorComponent implements OnInit, OnDestroy {
  selectedLanguage = this.translate.currentLang;
  langSub;
  storage;
  currentAppImage;
  currentTempAppImage;
  imageNotFound = false;
  generateImageForm;
  generatingImage = false;
  loading = false;
  AiModels = [
    { value: 'fast', viewValue: 'MySpace.ModelFast' },
    { value: 'pro', viewValue: 'MySpace.ModelPro' },
  ];

  types = [
    {
      index: 0,
      value: 'logo',
      viewValue: 'MySpace.LogoImage',
      folder: 'logos',
    },
    {
      index: 1,
      value: 'login',
      viewValue: 'MySpace.LoginImage',
      folder: 'login-images',
    },
  ];
  selectedType = 'login';
  selectedTabIndex = 0;
  isFreePlan = true;
  constructor(
    private translate: TranslateService,
    private _sanitizer: DomSanitizer,
    private env: EnvService,
    private fb: UntypedFormBuilder,
    private mySpaceService: MySpaceService,
    private toast: ToastService,
    private authService: AuthService,
    private confirmService: AppConfirmService,
    private arouter: ActivatedRoute,
    private chunkService: ChunkService,
    private router: Router
  ) {
    this.langSub = this.translate.onLangChange.subscribe((res) => {
      this.selectedLanguage = res.lang;
    });
    this.storage = inject(Storage);
    this.selectedType = this.arouter.snapshot.paramMap.get('tab') || 'login';
    this.selectedTabIndex = this.types.findIndex((e) => e.value == this.selectedType);
  }

  ngOnInit(): void {
    this.getImage();
    this.generateImageForm = this.fb.group({
      prompt: ['', [Validators.required]],
      model: ['fast', [Validators.required]],
    });
    this.mySpaceService.isPlanFree().then((result) => {
      this.isFreePlan = !!result;
      this.setCurrentTab();
    });
  }

  async getDownloadURL(url) {
    try {
      const reference = ref(this.storage, url);
      const result = await getDownloadURL(reference);
      this.imageNotFound = false;
      return result;
    } catch (error) {
      console.error(error);
      this.imageNotFound = true;
      return '';
    }
  }

  imageUploadedSucessfully(event) {
    this.getImage();
  }

  setCurrentTab() {
    let option = this.arouter.snapshot.paramMap.get('tab');
    if (option != 'menu' && this.isFreePlan) {
      option = 'menu';
    }
    this.selectedTabIndex = this.types.findIndex((e) => e.value == option);
  }

  tabChanged(tab) {
    this.router.navigateByUrl(`myspace/personalize-app/${this.types[tab.index].value}`);
    localStorage.setItem('personalizeAppTab', this.types[tab.index].value);
    setTimeout(() => {
      this.getImage();
    }, 200);
  }

  async getImage() {
    const folderName = this.types[this.selectedTabIndex].folder;
    if (!folderName) {
      return;
    }
    try {
      this.env.linkPrefix;
      const currentImageURL = await this.getDownloadURL(`${this.env.linkPrefix}ui.config/${folderName}/${this.env.appName}.jpg`);
      if (currentImageURL.length) {
        this.currentAppImage = this._sanitizer.bypassSecurityTrustResourceUrl(currentImageURL);
      } else {
        this.currentAppImage = null;
      }
    } catch (err) {}
    try {
      const userID = ((await this.authService.getUserData()) as any).id;
      const tempImageURL = await this.getDownloadURL(`${this.env.linkPrefix}ui.config/${folderName}/${this.env.appName}_${userID}.jpg`);
      if (tempImageURL.trim().length) {
        this.currentTempAppImage = this._sanitizer.bypassSecurityTrustResourceUrl(tempImageURL);
      } else {
        this.currentTempAppImage = null;
      }
    } catch (err) {}
  }
  ngOnDestroy(): void {
    this.langSub.unsubscribe();
  }
  generateImage() {
    this.generatingImage = true;
    const imageInfo = {
      type: this.types[this.selectedTabIndex],
      ...this.generateImageForm.value,
      aspectRatio: '1:1',
    };
    this.chunkService.generateImage(imageInfo).subscribe({
      complete: () => {
        this.generatingImage = false;
        this.getImage();
      },
      error: (err) => {
        this.generatingImage = false;
        console.error(err);
        if (err.error && err.error.msg == 'notEnoughCredits') {
          this.toast.snackbar.openFromComponent(NotEnoughCreditComponent, {
            data: {
              message: this.translate.instant('Toastr.notEnoughCredits'),
              linkText: this.translate.instant('Toastr.addCreditsHere'),
              linkUrl: '/myspace/credit',
            },
            duration: 10000,
          });
        }
      },
    });
  }

  async replaceImageWithUsers() {
    const response = await firstValueFrom(this.confirmService.confirm());
    if (!response) {
      return;
    }
    this.loading = true;
    this.mySpaceService.replaceFileWithTemp({ type: this.types[this.selectedTabIndex] }).subscribe({
      complete: () => {
        this.getImage();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  async deleteCurrentImage() {
    const response = await firstValueFrom(this.confirmService.confirm());
    if (!response) {
      return;
    }
    this.loading = true;
    this.mySpaceService.deleteCurrentImage({ type: this.types[this.selectedTabIndex] }).subscribe({
      complete: () => {
        this.getImage();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  getFullFolderName() {
    return `ui.config/${this.types[this.selectedTabIndex].folder}`;
  }
}
